<template>
  <a-modal
    :open="open"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
    width="800px"
    class="hospital-ad-modal"
  >
    <template #title>
      <div class="modal-title">
        {{ isEdit ? '광고 병원 수정' : '광고 병원 추가' }}
      </div>
    </template>

    <div class="modal-content">
      <a-form layout="vertical">
        <!-- 병원 -->
        <a-form-item label="병원" class="form-item">
          <a-select
            v-model:value="formData.companyId"
            show-search
            placeholder="병원명을 검색해주세요"
            :disabled="isEdit"
            :filter-option="false"
            :options="hospitalOptions"
            :not-found-content="searching ? '검색 중...' : '검색 결과가 없습니다'"
            @search="handleSearch"
          />
        </a-form-item>

        <!-- 광고 기간 -->
        <div class="form-row">
          <a-form-item label="광고 시작일" class="form-item half-width">
            <a-date-picker
              v-model:value="formData.startDate"
              format="YYYY-MM-DD"
              placeholder="시작일 선택"
              style="width: 100%"
            />
          </a-form-item>
          <a-form-item label="광고 마감일 (선택사항)" class="form-item half-width">
            <a-date-picker
              v-model:value="formData.endDate"
              format="YYYY-MM-DD"
              placeholder="마감일 없음"
              style="width: 100%"
            />
            <div class="featured-help">마감일 당일 23:59까지 광고합니다. 비워 두면 마감일 없이 계속 광고합니다.</div>
          </a-form-item>
        </div>

        <!-- 게시 여부 -->
        <a-form-item label="게시 여부" class="form-item">
          <a-switch
            v-model:checked="formData.isFeatured"
            checked-children="게시"
            un-checked-children="미게시"
          />
          <div class="featured-help">미게시로 두면 기간 안이어도 광고하지 않습니다.</div>
        </a-form-item>
      </a-form>

      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button
          type="primary"
          :loading="loading"
          @click="handleSubmit"
          class="yellow-button"
        >
          {{ isEdit ? '수정' : '추가' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive, watch } from 'vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import apiInstance from '@/axios';
import HospitalService from '@/views/CategoryManagement/service/HospitalService';

const SEARCH_DELAY_MS = 300;
const SEARCH_LIMIT = 20;

export default {
  name: 'HospitalAdModal',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    /** 수정할 광고 병원 줄 ({ companyId, name, region, startDate, endDate, isFeatured }) */
    row: {
      type: Object,
      default: null,
    },
  },
  emits: ['ok', 'cancel'],
  setup(props, { emit }) {
    const formData = reactive({
      companyId: undefined,
      startDate: null,
      endDate: null,
      isFeatured: true,
    });
    const hospitalOptions = ref([]);
    const searching = ref(false);
    let searchTimer = null;
    let searchSeq = 0;

    // 기다리는 검색과 아직 안 온 응답을 버린다 (검색어를 지우거나 창을 다시 열 때)
    const cancelSearch = () => {
      clearTimeout(searchTimer);
      searchSeq += 1;
      searching.value = false;
    };

    const initFormData = () => {
      cancelSearch();
      if (props.isEdit && props.row) {
        hospitalOptions.value = [
          { value: props.row.companyId, label: `${props.row.name} (${props.row.region})` },
        ];
        Object.assign(formData, {
          companyId: props.row.companyId,
          startDate: dayjs(props.row.startDate),
          endDate: props.row.endDate ? dayjs(props.row.endDate) : null,
          isFeatured: props.row.isFeatured,
        });
      } else {
        hospitalOptions.value = [];
        // 시작일 기본값은 오늘
        Object.assign(formData, {
          companyId: undefined,
          startDate: dayjs(),
          endDate: null,
          isFeatured: true,
        });
      }
    };

    // 병원 검색 (카테고리 관리 병원 검색 API 재사용, 늦게 온 응답은 버림)
    const handleSearch = (keyword) => {
      cancelSearch();
      const condition = keyword.trim();
      if (!condition) {
        hospitalOptions.value = [];
        return;
      }
      searchTimer = setTimeout(async () => {
        const seq = ++searchSeq;
        searching.value = true;
        try {
          const params = HospitalService.getSearchParams({
            currentPage: 1,
            rowsPerPage: SEARCH_LIMIT,
            condition,
          });
          const result = await HospitalService.getHospitalList(apiInstance, params);
          if (seq !== searchSeq) return;
          hospitalOptions.value = result.list.map((hospital) => ({
            value: hospital.id,
            label: hospital.address ? `${hospital.name} (${hospital.address})` : hospital.name,
          }));
        } catch (error) {
          if (seq === searchSeq) {
            message.error('병원을 검색하지 못했습니다. 잠시 후 다시 시도해주세요.');
          }
        } finally {
          if (seq === searchSeq) searching.value = false;
        }
      }, SEARCH_DELAY_MS);
    };

    const handleSubmit = () => {
      if (!formData.companyId) {
        message.warning('병원을 선택해주세요.');
        return;
      }
      if (!formData.startDate) {
        message.warning('시작일을 정해주세요.');
        return;
      }
      if (formData.endDate && formData.endDate.isBefore(formData.startDate, 'day')) {
        message.warning('마감일은 시작일과 같거나 이후로 정해주세요.');
        return;
      }
      emit('ok', {
        companyId: formData.companyId,
        startDate: formData.startDate.format('YYYY-MM-DD'),
        endDate: formData.endDate ? formData.endDate.format('YYYY-MM-DD') : null,
        isFeatured: formData.isFeatured,
      });
    };

    const handleCancel = () => {
      emit('cancel');
    };

    watch(
      () => props.open,
      (open) => {
        if (open) initFormData();
      },
    );

    return {
      formData,
      hospitalOptions,
      searching,
      handleSearch,
      handleSubmit,
      handleCancel,
    };
  },
};
</script>

<style scoped lang="scss">
/* BannerModal.vue 스타일 (이미지 업로드 제외) */
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;
}

.form-item {
  margin-bottom: 24px;

  :deep(.ant-form-item-label) {
    padding: 0 0 8px;

    label {
      color: #000;
      font-size: 14px;
      height: auto;
      font-weight: 500;
    }
  }

  :deep(.ant-form-item-control-input) {
    .ant-input,
    .ant-input-number,
    .ant-select-selector {
      height: 48px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      background: #f9fafb;

      &:hover,
      &:focus {
        border-color: #000;
        box-shadow: none;
      }
    }

    .ant-picker {
      height: 48px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      background: #f9fafb;
      width: 100%;

      &:hover,
      &.ant-picker-focused {
        border-color: #000;
        box-shadow: none;
      }
    }
  }
}

.form-row {
  display: flex;
  gap: 16px;

  .half-width {
    flex: 1;
  }
}

/* 도움말 (BannerTable .created-date 색·크기) */
.featured-help {
  font-size: 12px;
  color: #666;
  margin-top: 6px;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

@media screen and (max-width: 768px) {
  .modal-content {
    padding: 16px;
  }

  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .modal-footer {
    flex-direction: column;

    .ant-btn {
      width: 100%;
    }
  }
}
</style>
